import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { TimeSlotAnalysisResponse, TimeSlotDimension } from "../api/client";
import { formatRegionLabel } from "../utils/regions";
import { filterTimeSlotAnalysisBySegment, type DeviceTimeSlotScope, type GeoTimeSlotScope } from "../utils/time-slots";

const formatter = new Intl.NumberFormat("zh-HK");
const percentageFormatter = new Intl.NumberFormat("zh-HK", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const SERIES_COLORS = ["#1c2676", "#d97706", "#2563eb", "#7c3aed", "#be123c", "#475569", "#0891b2", "#65a30d"];
const WEEKDAY_ORDER = [
  { value: 1, label: "週一" },
  { value: 2, label: "週二" },
  { value: 3, label: "週三" },
  { value: 4, label: "週四" },
  { value: 5, label: "週五" },
  { value: 6, label: "週六" },
  { value: 0, label: "週日" }
];
const TIME_BUCKETS = [
  { label: "00-03", start: 0, end: 3 },
  { label: "04-07", start: 4, end: 7 },
  { label: "08-11", start: 8, end: 11 },
  { label: "12-15", start: 12, end: 15 },
  { label: "16-19", start: 16, end: 19 },
  { label: "20-23", start: 20, end: 23 }
];

function formatPercentage(value: number): string {
  return `${percentageFormatter.format(value)}%`;
}

function buildHourlyRows(timeSlots: TimeSlotAnalysisResponse | null) {
  const segments = timeSlots?.segments.results.map((row) => row.segment) ?? [];
  const rows = Array.from({ length: 24 }, (_, hour) => {
    const row: Record<string, string | number> = { hour, hourLabel: `${String(hour).padStart(2, "0")}:00` };
    for (const segment of segments) row[segment] = 0;
    return row;
  });

  for (const item of timeSlots?.hourly.results ?? []) {
    rows[item.hour][item.segment] = item.views;
  }

  return { rows, segments };
}

function buildWeeklyRows(timeSlots: TimeSlotAnalysisResponse | null) {
  const weeklyRows = WEEKDAY_ORDER.map((weekday) => ({
    weekday,
    buckets: TIME_BUCKETS.map((bucket) => {
      const segmentViews = new Map<string, number>();
      for (const row of timeSlots?.weekly.results ?? []) {
        if (row.weekday !== weekday.value || row.hour < bucket.start || row.hour > bucket.end) continue;
        segmentViews.set(row.segment, (segmentViews.get(row.segment) ?? 0) + row.views);
      }
      const entries = Array.from(segmentViews.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      const views = entries.reduce((sum, [, value]) => sum + value, 0);
      const topSegment = entries[0] ? { name: entries[0][0], views: entries[0][1] } : null;
      return { ...bucket, views, topSegment };
    })
  }));
  const maxViews = Math.max(1, ...weeklyRows.flatMap((row) => row.buckets.map((bucket) => bucket.views)));
  return { weeklyRows, maxViews };
}

export function TimeSlotsPage({
  data,
  dimension,
  deviceScope,
  geoScope,
  onDimensionChange,
  onDeviceScopeChange,
  onGeoScopeChange
}: {
  data: TimeSlotAnalysisResponse | null;
  dimension: TimeSlotDimension;
  deviceScope: DeviceTimeSlotScope;
  geoScope: GeoTimeSlotScope;
  onDimensionChange: (dimension: TimeSlotDimension) => void;
  onDeviceScopeChange: (scope: DeviceTimeSlotScope) => void;
  onGeoScopeChange: (scope: GeoTimeSlotScope) => void;
}) {
  const deviceSegments = dimension === "device" ? data?.segments.results.map((row) => row.segment) ?? [] : [];
  const displayData = dimension === "device" ? filterTimeSlotAnalysisBySegment(data, deviceScope) : data;
  const { rows: hourlyRows, segments } = buildHourlyRows(displayData);
  const { weeklyRows, maxViews } = buildWeeklyRows(displayData);
  const title = dimension === "device" ? "裝置" : "地區";
  const formatSegment = dimension === "geo" ? formatRegionLabel : (segment: string) => segment;
  const summaryRows = (displayData?.segments.results ?? []).map((row) => ({
    ...row,
    displaySegment: formatSegment(row.segment)
  }));
  const rangeLabel = data ? `${data.range.from} 至 ${data.range.to}` : "資料載入中";

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <h2>時段分析</h2>
        <div className="toolbar-actions">
          <div className="segmented" aria-label="時段交叉維度">
            <button type="button" className={dimension === "device" ? "active" : ""} onClick={() => onDimensionChange("device")}>
              裝置
            </button>
            <button type="button" className={dimension === "geo" ? "active" : ""} onClick={() => onDimensionChange("geo")}>
              地區
            </button>
          </div>
          {dimension === "device" ? (
            <div className="segmented" aria-label="裝置顯示範圍">
              <button type="button" className={deviceScope === "all" ? "active" : ""} onClick={() => onDeviceScopeChange("all")}>
                全部
              </button>
              {deviceSegments.map((segment) => (
                <button
                  key={segment}
                  type="button"
                  className={deviceScope === segment ? "active" : ""}
                  onClick={() => onDeviceScopeChange(segment)}
                >
                  {segment}
                </button>
              ))}
            </div>
          ) : null}
          {dimension === "geo" ? (
            <div className="segmented" aria-label="地區顯示範圍">
              <button type="button" className={geoScope === "top10" ? "active" : ""} onClick={() => onGeoScopeChange("top10")}>
                前十
              </button>
              <button type="button" className={geoScope === "all" ? "active" : ""} onClick={() => onGeoScopeChange("all")}>
                全部
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h3>{title}排名</h3>
            <span>{rangeLabel}</span>
          </div>
        </div>
        <div className="time-slot-summary">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={summaryRows} layout="vertical" margin={{ left: 28 }}>
              <CartesianGrid stroke="#e5e7eb" />
              <XAxis type="number" />
              <YAxis dataKey="displaySegment" type="category" width={150} />
              <Tooltip />
              <Bar dataKey="views" name="觀看" fill="#1c2676" />
            </BarChart>
          </ResponsiveContainer>
          <div className="table-wrap">
            <table className="compact-table">
              <thead>
                <tr>
                  <th>{title}</th>
                  <th>觀看</th>
                  <th>佔比</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row) => (
                  <tr key={row.segment}>
                    <td>{row.displaySegment}</td>
                    <td>{formatter.format(row.views)}</td>
                    <td>{formatPercentage(row.percentage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h3>24 小時分布</h3>
            <span>依{title}堆疊</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={hourlyRows}>
            <CartesianGrid stroke="#e5e7eb" />
            <XAxis dataKey="hourLabel" tickMargin={8} interval={1} />
            <YAxis width={54} />
            <Tooltip />
            {segments.map((segment, index) => (
              <Bar key={segment} dataKey={segment} name={formatSegment(segment)} stackId="time-slot" fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h3>每週時段熱度</h3>
            <span>格內顯示最高{title}</span>
          </div>
        </div>
        <div className="table-wrap">
          <table className="time-heatmap-table time-slot-heatmap">
            <thead>
              <tr>
                <th>星期</th>
                {TIME_BUCKETS.map((bucket) => (
                  <th key={bucket.label}>{bucket.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeklyRows.map((row) => (
                <tr key={row.weekday.value}>
                  <th>{row.weekday.label}</th>
                  {row.buckets.map((bucket) => {
                    const heat = bucket.views / maxViews;
                    return (
                      <td key={bucket.label}>
                        <span className="heatmap-cell time-slot-cell" style={{ backgroundColor: `rgba(28, 38, 118, ${0.08 + heat * 0.62})` }}>
                          <strong>{formatter.format(bucket.views)}</strong>
                          <small>{bucket.topSegment ? `${formatSegment(bucket.topSegment.name)} ${formatter.format(bucket.topSegment.views)}` : "-"}</small>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
